import { TFunction } from "i18next";
import { IIncidentActivity } from "../interfaces/incident/IIncident";

const INCIDENT_TYPE_IMAGES: Record<string, string> = {
  ACTIVE_SHOOTER: "/assets/images/active_shooter.png",
  KNIFE_ATTACK: "/assets/images/knife_attack.png",
  VIOLENT_ACTIVITY: "/assets/images/violent_activity.png",
  SECURITY_THREAT: "/assets/images/violent_activity.png",
  FIRE_EMERGENCY: "/assets/images/fire_emergency.png",
  FIRE: "/assets/images/fire_emergency.png",
};

// Only some incident types have artwork; callers decide what to show otherwise
export const getIncidentTypeImagePath = (type?: string): string | undefined =>
  type ? INCIDENT_TYPE_IMAGES[type.toUpperCase()] : undefined;

export interface IActivityDisplay {
  icon: string;
  imageSrc?: string;
  message: string;
}

const getSafetyStatusMessage = (activity: IIncidentActivity, t: TFunction) => {
  const statusText = activity.safetyStatusType
    ? t(`incident.safetyStatusType.${activity.safetyStatusType}`)
    : t("general.notAvailable");
  return `${t("incident.updatedStatusTo")} "${statusText}"`;
};

const getIncidentReportDisplay = (
  t: TFunction,
  incidentType?: string,
): IActivityDisplay => ({
  icon: "ti ti-alert-triangle",
  imageSrc: getIncidentTypeImagePath(incidentType),
  message: incidentType
    ? `${t("incident.reportedIncident")}: ${t(`incident.type.${incidentType}`, {
        defaultValue: incidentType,
      })}`
    : t("incident.reportedIncident"),
});

/**
 * Icon / picture and text for one timeline entry. Incident reports show the
 * incident type's picture so it is clear what was reported.
 */
export const getActivityDisplay = (
  activity: IIncidentActivity,
  t: TFunction,
  incidentType?: string,
): IActivityDisplay => {
  const activityType = (activity.activityType || "").toUpperCase();

  if (activityType === "INCIDENT_REPORT") {
    return getIncidentReportDisplay(t, incidentType);
  }
  if (activityType === "STATUS_REPORT") {
    return { icon: "ti ti-shield-check", message: getSafetyStatusMessage(activity, t) };
  }
  switch (activity.mediaType) {
    case "VOICE_RECORD":
      return { icon: "ti ti-volume", message: t("incident.sentAudio") };
    case "IMAGE":
      return { icon: "ti ti-photo", message: t("incident.sentImage") };
    case "VIDEO":
      return { icon: "ti ti-video", message: t("incident.sentVideo") };
  }
  if (activity.safetyStatusType) {
    return { icon: "ti ti-shield-check", message: getSafetyStatusMessage(activity, t) };
  }
  return { icon: "ti ti-message", message: t("incident.sentMessage") };
};
